import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { AxiosResponse } from "axios";
import { Api } from "services/api/api.service";
import { useAuthStore } from "stores/authStore/authStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { DOCUMENT_FRONTEND_ROOT, API_ROOT } from "services/baseURLs.config";
import type {
  StaffProfileData,
  CompanyWorkerDocumentResponse,
} from "services/api/company/staff.service";
import type {
  GetDocumentResponse,
  CreateDocumentRequest,
} from "services/document-service/document.service";
import { generateEmploymentAgreementPdf } from "./utils/pdfGenerator";

interface UseAgreementsSectionLogicParams {
  companyId: number | null;
  workerId: number | null;
  profile: StaffProfileData | null;
}

export interface AgreementItem {
  doc: CompanyWorkerDocumentResponse;
  details: GetDocumentResponse | null;
  signingUrl: string | null;
}

const formatTo12DigitSsn = (identityNumber: string | null): string => {
  if (!identityNumber) return "";
  const cleaned = identityNumber.replace(/[^0-9]/g, "");
  if (cleaned.length === 10) {
    const year = parseInt(cleaned.substring(0, 2), 10);
    const prefix = year > 30 ? "19" : "20";
    return prefix + cleaned;
  }
  return cleaned;
};

export const CONTRACT_EMPLOYMENT_TYPE_OPTIONS = [
  { value: "Tillsvidare", label: "Tillsvidare" },
  { value: "Särskild visstid", label: "Särskild visstid" },
  { value: "Vikariat", label: "Vikariat" },
  { value: "Säsongsarbete", label: "Säsongsarbete" },
  { value: "Provanställning", label: "Provanställning" },
];

export const WORK_SCHEDULE_ARRANGEMENT_OPTIONS = [
  { value: "Enligt schema", label: "Enligt schema" },
  { value: "Fast arbetstid", label: "Fast arbetstid" },
  { value: "Varierande arbetstid", label: "Varierande arbetstid" },
];

export const PROBATION_UNIT_OPTIONS = [
  { value: "månader", label: "månader" },
  { value: "veckor", label: "veckor" },
];

export const mapProfileEmploymentTypeToContractType = (
  rawType?: string | null,
): string => {
  if (!rawType) return "Tillsvidare";
  const lower = rawType.toLowerCase();
  if (
    lower.includes("introductory") ||
    lower.includes("provanställning") ||
    lower.includes("prov")
  ) {
    return "Provanställning";
  }
  if (
    lower.includes("temporary") ||
    lower.includes("timanställd") ||
    lower.includes("visstid") ||
    lower.includes("extra")
  ) {
    return "Särskild visstid";
  }
  if (lower.includes("project") || lower.includes("säsong")) {
    return "Säsongsarbete";
  }
  if (lower.includes("vikariat") || lower.includes("vikarie")) {
    return "Vikariat";
  }
  return "Tillsvidare";
};

export const formatNoticeDays = (
  days: number | string | undefined | null,
): string => {
  if (days == null || days === "") return "30 dagar";
  const str = String(days).trim();
  if (/^\d+$/.test(str)) {
    return `${str} dagar`;
  }
  return str;
};

export const calculateWeeklyHours = (percentageStr: string): string => {
  const numericPct = parseInt(percentageStr.replace(/[^0-9]/g, ""), 10);
  if (isNaN(numericPct) || numericPct <= 0) {
    return "40 timmar/vecka";
  }
  const hours = Math.round((numericPct / 100) * 40 * 10) / 10;
  return `${hours} timmar/vecka`;
};

export const calculatePartTimeSalary = (
  fullTimeSalaryStr: string,
  percentageStr: string,
): string => {
  const numBase = parseInt(fullTimeSalaryStr.replace(/[^0-9]/g, ""), 10);
  const numPct = parseInt(percentageStr.replace(/[^0-9]/g, ""), 10);
  if (isNaN(numBase) || numBase <= 0 || isNaN(numPct) || numPct <= 0) {
    return "";
  }
  const partTime = Math.round((numBase * numPct) / 100);
  return partTime.toLocaleString("sv-SE");
};

export const isPartTimeWorkPercentage = (percentageStr: string): boolean => {
  const numPct = parseInt(percentageStr.replace(/[^0-9]/g, ""), 10);
  return !isNaN(numPct) && numPct > 0 && numPct < 100;
};

export const getCalculatedProbationEndDate = (
  start: string,
  durationStr: string,
  unit: string,
): string => {
  if (!start || !moment(start, ["YYYY-MM-DD", moment.ISO_8601], true).isValid()) return "";
  const duration = parseInt(durationStr, 10) || 6;
  if (unit === "veckor") {
    return moment(start).add(duration, "weeks").subtract(1, "day").format("YYYY-MM-DD");
  }
  return moment(start).add(duration, "months").subtract(1, "day").format("YYYY-MM-DD");
};

const getAgreementTimestamp = (item: AgreementItem): number => {
  const dateStr = item.details?.created_at;

  if (dateStr && moment(dateStr).isValid()) {
    return moment(dateStr).valueOf();
  }
  return 0;
};

export const useAgreementsSectionLogic = ({
  companyId,
  workerId,
  profile,
}: UseAgreementsSectionLogicParams) => {
  const { t } = useTranslation();
  const [authState] = useAuthStore();
  const [agreements, setAgreements] = useState<AgreementItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newAgreementName, setNewAgreementName] = useState("");
  const [employerSsn, setEmployerSsn] = useState("");
  const [employerName, setEmployerName] = useState("");
  const [customFile, setCustomFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [createdSigningUrl, setCreatedSigningUrl] = useState<string | null>(
    null,
  );

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocumentName, setUploadDocumentName] = useState("");
  const [uploadCategory, setUploadCategory] = useState("employment");
  const [uploadNotes, setUploadNotes] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [companyState] = useCompanyStore();
  const [collectiveAgreement, setCollectiveAgreement] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [employmentType, setEmploymentType] = useState("Tillsvidare");
  const [probationDuration, setProbationDuration] = useState("6");
  const [probationUnit, setProbationUnit] = useState("månader");
  const [probationEndDate, setProbationEndDate] = useState("");
  const [workPercentage, setWorkPercentage] = useState("100%");
  const [weeklyHours, setWeeklyHours] = useState("40 timmar/vecka");
  const [workScheduleArrangement, setWorkScheduleArrangement] =
    useState("Enligt schema");
  const [salary, setSalary] = useState("");
  const [salaryType, setSalaryType] = useState("");
  const [vacationDays, setVacationDays] = useState("25");
  const [noticeDays, setNoticeDays] = useState("30 dagar");
  const [workPlace, setWorkPlace] = useState("");
  const [otherTerms, setOtherTerms] = useState("");

  const handleWorkPercentageChange = (value: string) => {
    setWorkPercentage(value);
    setWeeklyHours(calculateWeeklyHours(value));
  };

  const handleEmploymentTypeChange = (type: string) => {
    setEmploymentType(type);
    if (type === "Provanställning") {
      setEndDate("");
      if (startDate) {
        setProbationEndDate(
          getCalculatedProbationEndDate(startDate, probationDuration, probationUnit),
        );
      }
    } else if (type === "Tillsvidare") {
      setEndDate("");
      setProbationEndDate("");
    }
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    if (employmentType === "Provanställning") {
      setProbationEndDate(
        getCalculatedProbationEndDate(date, probationDuration, probationUnit),
      );
    }
  };

  const handleProbationDurationChange = (dur: string) => {
    setProbationDuration(dur);
    if (startDate) {
      setProbationEndDate(
        getCalculatedProbationEndDate(startDate, dur, probationUnit),
      );
    }
  };

  const handleProbationUnitChange = (unit: string) => {
    setProbationUnit(unit);
    if (startDate) {
      setProbationEndDate(
        getCalculatedProbationEndDate(startDate, probationDuration, unit),
      );
    }
  };

  const isHourly =
    salaryType === "Hourly" ||
    salaryType === "Timlön" ||
    (employmentType !== undefined &&
      (employmentType.toLowerCase().includes("tim") ||
        employmentType.toLowerCase().includes("behov") ||
        employmentType.toLowerCase().includes("visstid") ||
        employmentType === "ExclusiveTemporaryWorker"));

  const isPartTime =
    !isHourly && isPartTimeWorkPercentage(workPercentage);
  const calculatedPartTimeSalary = isPartTime
    ? calculatePartTimeSalary(salary, workPercentage)
    : "";

  useEffect(() => {
    const companyInfo = companyState.company;
    const companyName =
      companyInfo?.companyName || companyInfo?.legalName || "";
    const companyAddress = companyInfo?.address || "";
    const defaultWorkPlace = [companyName, companyAddress]
      .filter(Boolean)
      .join(", ");

    if (!employerName) {
      setEmployerName(
        `${authState.user?.firstName || ""} ${authState.user?.lastName || ""}`.trim(),
      );
    }

    if (profile?.employmentInfo?.selected) {
      const emp = profile.employmentInfo.selected;
      setRoleTitle(emp.role || "");
      const companySettings = companyInfo?.companySettings || [];
      const tzSetting = companySettings.find(
        (s) => s.key === "CompanyTimeZone",
      );
      const tz = tzSetting?.value || "Europe/Stockholm";
      const startStr = emp.startDate
        ? moment.tz(emp.startDate, tz).format("YYYY-MM-DD")
        : "";
      setStartDate(startStr);

      const mappedType = mapProfileEmploymentTypeToContractType(
        emp.employmentType,
      );
      setEmploymentType(mappedType);
      setProbationDuration("6");
      setProbationUnit("månader");

      if (mappedType === "Provanställning") {
        const pEnd = emp.endDate
          ? moment.tz(emp.endDate, tz).format("YYYY-MM-DD")
          : getCalculatedProbationEndDate(startStr, "6", "månader");
        setProbationEndDate(pEnd);
        setEndDate("");
      } else if (mappedType === "Tillsvidare") {
        setProbationEndDate("");
        setEndDate("");
      } else {
        setProbationEndDate("");
        setEndDate(
          emp.endDate ? moment.tz(emp.endDate, tz).format("YYYY-MM-DD") : "",
        );
      }

      const pct = emp.workPercentage
        ? Math.round(Number(emp.workPercentage) * 100)
        : 100;
      const pctStr = `${pct}%`;
      setWorkPercentage(pctStr);
      setWeeklyHours(calculateWeeklyHours(pctStr));
      setWorkScheduleArrangement("Enligt schema");

      setSalary(emp.salary ? String(emp.salary) : "");

      let salTypeStr = "";
      if (emp.salaryType) {
        if (emp.salaryType === "Hourly") {
          salTypeStr = "Timlön";
        } else if (emp.salaryType === "Monthly") {
          salTypeStr = "Månadslön";
        } else {
          salTypeStr = emp.salaryType;
        }
      }
      setSalaryType(salTypeStr);

      setVacationDays(emp.vacationDays ? String(emp.vacationDays) : "25");
      setNoticeDays(formatNoticeDays(emp.noticeDays));
      setWorkPlace(emp.workplace || defaultWorkPlace);
      setCollectiveAgreement(emp.agreement || "");
    } else {
      setRoleTitle("");
      setStartDate("");
      setEndDate("");
      setEmploymentType("Tillsvidare");
      setProbationDuration("6");
      setProbationUnit("månader");
      setProbationEndDate("");
      setWorkPercentage("100%");
      setWeeklyHours("40 timmar/vecka");
      setWorkScheduleArrangement("Enligt schema");
      setSalary("");
      setSalaryType("");
      setVacationDays("25");
      setNoticeDays("30 dagar");
      setWorkPlace(defaultWorkPlace);
      setCollectiveAgreement("");
    }
  }, [profile, companyState.company, authState.user, employerName]);


  const fetchAgreements = useCallback(async () => {
    if (companyId == null || workerId == null || !profile) return;
    setLoading(true);
    try {
      const response = await Api().company.staff.getCompanyWorkerDocuments(
        companyId,
        workerId,
      );
      const docs: CompanyWorkerDocumentResponse[] = response.data;

      const companyUserId = authState.user?.id
        ? String(authState.user.id)
        : undefined;

      const items = await Promise.all(
        docs.map(async (doc) => {
          if (!doc.documentId) {
            return { doc, details: null, signingUrl: null };
          }
          try {
            const detailsRes: AxiosResponse<GetDocumentResponse> =
              await Api().document.getDocument(doc.documentId, {
                companyId: String(companyId),
                companyUserId,
              });
            const details = detailsRes.data;

            let signingUrl: string | null = null;
            if (doc.status?.toLowerCase() !== "completed") {
              // Find worker participant by name or fall back to first participant
              const workerName =
                `${profile.firstName || ""} ${profile.lastName || ""}`
                  .trim()
                  .toLowerCase();
              const workerParticipant =
                details.participants?.find(
                  (p) => p.name?.trim().toLowerCase() === workerName,
                ) || details.participants?.[0];

              const participantId = workerParticipant?.id;
              signingUrl = participantId
                ? `${DOCUMENT_FRONTEND_ROOT}/document/${doc.documentId}?participantId=${participantId}`
                : null;
            }

            return { doc, details, signingUrl };
          } catch (err) {
            console.error(
              "Error fetching document details for",
              doc.documentId,
              err,
            );
            return { doc, details: null, signingUrl: null };
          }
        }),
      );

      // Filter out expired agreements that are older than 14 days
      const fourteenDaysAgo = moment().subtract(14, "days");
      const filteredItems = items.filter((item) => {
        const isExpired =
          item.doc.status?.toLowerCase() === "expired" ||
          item.details?.status?.toLowerCase() === "expired";

        if (!isExpired) return true;

        const dateStr = item.details?.expires_at || item.details?.created_at;

        if (dateStr && moment(dateStr).isValid()) {
          return !moment(dateStr).isBefore(fourteenDaysAgo);
        }

        return false;
      });

      // Sort by date (newest contract first), falling back to ID descending
      filteredItems.sort((a, b) => {
        const timeA = getAgreementTimestamp(a);
        const timeB = getAgreementTimestamp(b);
        if (timeA !== timeB) {
          return timeB - timeA;
        }
        return (b.doc.id || 0) - (a.doc.id || 0);
      });

      setAgreements(filteredItems);
    } catch (err) {
      console.error("Error loading worker documents:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, workerId, profile]);

  useEffect(() => {
    void fetchAgreements();
  }, [fetchAgreements]);

  const handleCreateAgreement = async () => {
    if (companyId == null || workerId == null || !profile) return;
    if (!newAgreementName.trim()) {
      setSaveError("Document name is required");
      return;
    }

    setIsSaving(true);
    setSaveError("");

    try {
      if (
        employmentType === "Provanställning" &&
        (!probationDuration || !probationDuration.trim())
      ) {
        throw new Error("Staff.ProfilePanelProbationDurationRequired");
      }

      let fileToSend: File | Blob | undefined = customFile || undefined;
      if (!fileToSend) {
        const companyInfo = companyState.company;
        const companyName =
          companyInfo?.companyName ||
          companyInfo?.legalName ||
          "Gigleer Client Company";
        const companyOrgNo = companyInfo?.registrationNumber || "-";
        const companyAddress =
          [companyInfo?.address, companyInfo?.postalCode, companyInfo?.city]
            .filter(Boolean)
            .join(", ") || "-";

        const workerName =
          `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
        const workerSsn = formatTo12DigitSsn(profile.personalIdentityNumber);
        const workerAddress =
          [profile.address, profile.postalCode, profile.city]
            .filter(Boolean)
            .join(", ") || "-";
        const workerEmail = profile.email || "";
        const workerPhone = profile.phoneNumber || "-";

        const pdfBytes = await generateEmploymentAgreementPdf({
          companyName,
          companyOrgNo,
          companyAddress,
          workerName,
          workerSsn,
          workerAddress,
          workerEmail,
          workerPhone,
          roleTitle,
          startDate,
          endDate:
            employmentType !== "Provanställning" &&
            employmentType !== "Tillsvidare"
              ? endDate || undefined
              : undefined,
          employmentType,
          probationDuration:
            employmentType === "Provanställning" ? probationDuration : undefined,
          probationUnit:
            employmentType === "Provanställning" ? probationUnit : undefined,
          probationEndDate:
            employmentType === "Provanställning"
              ? probationEndDate || undefined
              : undefined,
          probationBecomes:
            employmentType === "Provanställning"
              ? "Tillsvidareanställning"
              : undefined,
          workPercentage,
          weeklyHours,
          workScheduleArrangement,
          salary,
          salaryType,
          fullTimeSalary: isPartTime ? salary : undefined,
          partTimeSalary: isPartTime ? calculatedPartTimeSalary : undefined,
          vacationDays,
          noticeDays,
          workPlace,
          collectiveAgreement,
          otherTerms: otherTerms || undefined,
        });

        fileToSend = new File([pdfBytes as any], "anstallningsavtal.pdf", {
          type: "application/pdf",
        });
      }

      if (!employerName.trim()) {
        throw new Error("Staff.ProfilePanelEmployerNameRequired");
      }

      if (!employerSsn.trim()) {
        throw new Error("Staff.ProfilePanelEmployerSsnRequired");
      }

      const formattedEmployerSsn = formatTo12DigitSsn(employerSsn);
      if (!/^[0-9]{12}$/.test(formattedEmployerSsn)) {
        throw new Error("Staff.ProfilePanelEmployerSsnInvalid");
      }

      const workerSsn = formatTo12DigitSsn(profile.personalIdentityNumber);
      const workerName =
        `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
      const workerEmail = profile.email || "";

      if (!workerSsn || !workerName || !workerEmail) {
        throw new Error(
          "Worker profile is missing personal identity number, name, or email required for signing.",
        );
      }

      const employerParticipant = {
        externalId: String(authState.user?.id || ""),
        ssn: formattedEmployerSsn,
        name: employerName.trim(),
        email: authState.user?.email || "",
        metadata: {
          type: "company" as const,
          companyId: Number(companyId),
          userId: authState.user?.id || 0,
        },
      };

      const workerParticipant = {
        externalId: profile.externalId || String(workerId),
        ssn: workerSsn,
        name: workerName,
        email: workerEmail,
        metadata: {
          type: "worker" as const,
          workerId: Number(workerId),
          userId: Number(workerId),
        },
      };

      const requestData: CreateDocumentRequest = {
        file: fileToSend,
        participants: [employerParticipant, workerParticipant],
        initiator: employerParticipant.externalId,
        documentName: newAgreementName,
        category: "employment",
        contractType: "employment",
        callback: {
          url: API_ROOT,
          path: "/documents/integrations/callback",
        },
      };

      const companyUserId = authState.user?.id
        ? String(authState.user.id)
        : undefined;

      const response = await Api().document.createDocument(requestData, {
        companyId: String(companyId),
        companyUserId,
      });

      const newDocId = response.data.id;
      const employerPartId = response.data.participants?.[0]?.id;
      const signingUrl = employerPartId
        ? `${DOCUMENT_FRONTEND_ROOT}/document/${newDocId}?participantId=${employerPartId}`
        : null;

      if (signingUrl) {
        setCreatedSigningUrl(signingUrl);
      } else {
        setNewAgreementName("");
        setEmployerSsn("");
        setCustomFile(null);
        setIsCreateModalOpen(false);
      }
      void fetchAgreements();
    } catch (err: any) {
      console.error("Error creating agreement:", err);
      setSaveError(err.message || "Failed to create agreement");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadSignedAgreement = async (
    documentId: string,
    documentName: string | null,
  ) => {
    if (companyId == null || workerId == null) return;
    try {
      const companyUserId = authState.user?.id
        ? String(authState.user.id)
        : undefined;

      const response = await Api().document.getSignedPdf(documentId, {
        companyId: String(companyId),
        companyUserId,
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${documentName || "agreement"}_signed.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading signed PDF:", err);
    }
  };

  const handleCopySigningLink = (signingUrl: string, docId: string) => {
    void navigator.clipboard.writeText(signingUrl);
    setCopySuccess(docId);
    setTimeout(() => {
      setCopySuccess(null);
    }, 2000);
  };

  const resetForm = () => {
    const companyInfo = companyState.company;
    const companyName =
      companyInfo?.companyName || companyInfo?.legalName || "";
    const companyAddress = companyInfo?.address || "";
    const defaultWorkPlace = [companyName, companyAddress]
      .filter(Boolean)
      .join(", ");

    setNewAgreementName("");
    setEmployerSsn("");
    setEmployerName(
      `${authState.user?.firstName || ""} ${authState.user?.lastName || ""}`.trim(),
    );
    setCustomFile(null);
    setCreatedSigningUrl(null);
    if (profile?.employmentInfo?.selected) {
      const emp = profile.employmentInfo.selected;
      setRoleTitle(emp.role || "");
      const companySettings = companyInfo?.companySettings || [];
      const tzSetting = companySettings.find(
        (s) => s.key === "CompanyTimeZone",
      );
      const tz = tzSetting?.value || "Europe/Stockholm";
      const startStr = emp.startDate
        ? moment.tz(emp.startDate, tz).format("YYYY-MM-DD")
        : "";
      setStartDate(startStr);

      const mappedType = mapProfileEmploymentTypeToContractType(
        emp.employmentType,
      );
      setEmploymentType(mappedType);
      setProbationDuration("6");
      setProbationUnit("månader");

      if (mappedType === "Provanställning") {
        const pEnd = emp.endDate
          ? moment.tz(emp.endDate, tz).format("YYYY-MM-DD")
          : getCalculatedProbationEndDate(startStr, "6", "månader");
        setProbationEndDate(pEnd);
        setEndDate("");
      } else if (mappedType === "Tillsvidare") {
        setProbationEndDate("");
        setEndDate("");
      } else {
        setProbationEndDate("");
        setEndDate(
          emp.endDate ? moment.tz(emp.endDate, tz).format("YYYY-MM-DD") : "",
        );
      }

      const pct = emp.workPercentage
        ? Math.round(Number(emp.workPercentage) * 100)
        : 100;
      const pctStr = `${pct}%`;
      setWorkPercentage(pctStr);
      setWeeklyHours(calculateWeeklyHours(pctStr));
      setWorkScheduleArrangement("Enligt schema");

      setSalary(emp.salary ? String(emp.salary) : "");

      let salTypeStr = "";
      if (emp.salaryType) {
        if (emp.salaryType === "Hourly") {
          salTypeStr = "Timlön";
        } else if (emp.salaryType === "Monthly") {
          salTypeStr = "Månadslön";
        } else {
          salTypeStr = emp.salaryType;
        }
      }
      setSalaryType(salTypeStr);

      setVacationDays(emp.vacationDays ? String(emp.vacationDays) : "25");
      setNoticeDays(formatNoticeDays(emp.noticeDays));
      setWorkPlace(emp.workplace || defaultWorkPlace);
      setCollectiveAgreement(emp.agreement || "");
    } else {
      setRoleTitle("");
      setStartDate("");
      setEndDate("");
      setEmploymentType("Tillsvidare");
      setProbationDuration("6");
      setProbationUnit("månader");
      setProbationEndDate("");
      setWorkPercentage("100%");
      setWeeklyHours("40 timmar/vecka");
      setWorkScheduleArrangement("Enligt schema");
      setSalary("");
      setSalaryType("");
      setVacationDays("25");
      setNoticeDays("30 dagar");
      setWorkPlace(defaultWorkPlace);
      setCollectiveAgreement("");
    }
    setOtherTerms("");
  };

  const handlePreviewAgreement = async () => {
    try {
      let blobUrl = "";
      if (customFile) {
        blobUrl = URL.createObjectURL(customFile);
      } else {
        const companyInfo = companyState.company;
        const companyName =
          companyInfo?.companyName ||
          companyInfo?.legalName ||
          "Gigleer Client Company";
        const companyOrgNo = companyInfo?.registrationNumber || "-";
        const companyAddress =
          [companyInfo?.address, companyInfo?.postalCode, companyInfo?.city]
            .filter(Boolean)
            .join(", ") || "-";
        const workerName =
          `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim();
        const workerSsn = formatTo12DigitSsn(
          profile?.personalIdentityNumber || null,
        );
        const workerAddress =
          [profile?.address, profile?.postalCode, profile?.city]
            .filter(Boolean)
            .join(", ") || "-";
        const workerEmail = profile?.email || "";
        const workerPhone = profile?.phoneNumber || "-";

        const pdfBytes = await generateEmploymentAgreementPdf({
          companyName,
          companyOrgNo,
          companyAddress,
          workerName,
          workerSsn,
          workerAddress,
          workerEmail,
          workerPhone,
          roleTitle,
          startDate,
          endDate:
            employmentType !== "Provanställning" &&
            employmentType !== "Tillsvidare"
              ? endDate || undefined
              : undefined,
          employmentType,
          probationDuration:
            employmentType === "Provanställning" ? probationDuration : undefined,
          probationUnit:
            employmentType === "Provanställning" ? probationUnit : undefined,
          probationEndDate:
            employmentType === "Provanställning"
              ? probationEndDate || undefined
              : undefined,
          probationBecomes:
            employmentType === "Provanställning"
              ? "Tillsvidareanställning"
              : undefined,
          workPercentage,
          weeklyHours,
          workScheduleArrangement,
          salary,
          salaryType,
          fullTimeSalary: isPartTime ? salary : undefined,
          partTimeSalary: isPartTime ? calculatedPartTimeSalary : undefined,
          vacationDays,
          noticeDays,
          workPlace,
          collectiveAgreement,
          otherTerms: otherTerms || undefined,
        });

        const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
        blobUrl = URL.createObjectURL(blob);
      }
      if (blobUrl) {
        window.open(blobUrl, "_blank");
      }
    } catch (err) {
      console.error("Failed to generate PDF preview:", err);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadDocumentName("");
    setUploadCategory("employment");
    setUploadNotes("");
    setUploadError("");
  };

  const handleUploadAgreement = async () => {
    if (companyId == null || workerId == null || !profile) return;
    if (!uploadFile) {
      setUploadError(
        t(
          "Staff.ProfilePanelUploadFileRequired",
          "Vänligen välj en PDF-fil att ladda upp",
        ),
      );
      return;
    }

    const employerName =
      `${authState.user?.firstName || ""} ${authState.user?.lastName || ""}`.trim() ||
      undefined;

    const workerSsn = profile.personalIdentityNumber
      ? formatTo12DigitSsn(profile.personalIdentityNumber)
      : undefined;
    const workerName =
      `${profile.firstName || ""} ${profile.lastName || ""}`.trim() ||
      undefined;
    const workerEmail = profile.email || undefined;

    setIsUploading(true);
    setUploadError("");

    try {
      const employerExternalId = String(
        authState.user?.id || companyId || "company-admin",
      );
      const employerParticipant = {
        externalId: employerExternalId,
        name: employerName,
        email: authState.user?.email || undefined,
        metadata: {
          type: "company" as const,
          companyId: Number(companyId),
          userId: authState.user?.id || 0,
        },
      };

      const workerExternalId = String(
        profile.externalId || workerId || "worker",
      );
      const workerParticipant = {
        externalId: workerExternalId,
        ssn: workerSsn,
        name: workerName,
        email: workerEmail,
        metadata: {
          type: "worker" as const,
          workerId: Number(workerId),
          userId: Number(workerId),
        },
      };

      const companyUserId = authState.user?.id
        ? String(authState.user.id)
        : undefined;

      await Api().document.importDocument(
        {
          file: uploadFile,
          initiator: employerParticipant.externalId,
          documentName:
            uploadDocumentName.trim() ||
            uploadFile.name.replace(/\.pdf$/i, ""),
          category: uploadCategory || "employment",
          contractType: uploadCategory || "employment",
          notes: uploadNotes.trim() || undefined,
          participants: [employerParticipant, workerParticipant],
          callback: {
            url: API_ROOT,
            path: "/documents/integrations/callback",
          },
        },
        {
          companyId: String(companyId),
          companyUserId,
        },
      );

      resetUploadForm();
      setIsUploadModalOpen(false);
      void fetchAgreements();
    } catch (err: any) {
      console.error("Error uploading document:", err);
      const apiErr = err.response?.data?.error || err.message;
      setUploadError(apiErr || "Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  return {
    agreements,
    loading,
    isCreateModalOpen,
    newAgreementName,
    employerSsn,
    employerName,
    customFile,
    isSaving,
    saveError,
    copySuccess,
    createdSigningUrl,
    isUploadModalOpen,
    uploadFile,
    uploadDocumentName,
    uploadCategory,
    uploadNotes,
    isUploading,
    uploadError,
    roleTitle,
    startDate,
    endDate,
    collectiveAgreement,
    employmentType,
    probationDuration,
    probationUnit,
    probationEndDate,
    workPercentage,
    weeklyHours,
    workScheduleArrangement,
    salary,
    salaryType,
    isPartTime,
    calculatedPartTimeSalary,
    vacationDays,
    noticeDays,
    workPlace,
    otherTerms,
    setIsCreateModalOpen,
    setNewAgreementName,
    setEmployerSsn,
    setEmployerName,
    setCustomFile,
    setCreatedSigningUrl,
    setIsUploadModalOpen,
    setUploadFile,
    setUploadDocumentName,
    setUploadCategory,
    setUploadNotes,
    setRoleTitle,
    setStartDate,
    setEndDate,
    setEmploymentType,
    setProbationDuration,
    setProbationUnit,
    setProbationEndDate,
    handleEmploymentTypeChange,
    handleStartDateChange,
    handleProbationDurationChange,
    handleProbationUnitChange,
    setWorkPercentage,
    setWeeklyHours,
    setWorkScheduleArrangement,
    handleWorkPercentageChange,
    setSalary,
    setSalaryType,
    setVacationDays,
    setNoticeDays,
    setWorkPlace,
    setCollectiveAgreement,
    setOtherTerms,
    resetForm,
    resetUploadForm,
    handlePreviewAgreement,
    handleCreateAgreement,
    handleUploadAgreement,
    handleDownloadSignedAgreement,
    handleCopySigningLink,
    fetchAgreements,
  };
};
